/**
 * LLM model catalog — the single source of truth for which models the Argo
 * agent worker can be pointed at, across providers. The Admin → Settings
 * "Model" picker renders this list; `src/lib/agent/argoWorker.ts` sends the
 * chosen id to the worker on every call; the worker (`worker/llm.mjs`) keeps a
 * mirror of these ids as its allow-list and routes by provider.
 *
 * The worker is provider-agnostic: an id here is dispatched to Anthropic,
 * OpenAI, or Google Gemini by `providerForModel`. Adding a model is a one-line
 * change here + the same id in the worker allow-list — no UI rewiring.
 *
 * Frontier model ids verified current 2026-07 against each provider's own API
 * docs. This is a fast-moving list — when a provider ships a new flagship,
 * update this file and the worker mirror together.
 */

export type LlmProvider = 'anthropic' | 'openai' | 'google';

export interface LlmModel {
  /** Exact API model id sent on the wire. */
  id: string;
  /** Short display label for the picker. */
  label: string;
  /** Which vendor SDK the worker routes this id to. */
  provider: LlmProvider;
  /** One line: when an operator would pick this. */
  blurb: string;
  /** The suggested default for a fresh instance. */
  recommended?: boolean;
}

export const PROVIDER_LABEL: Record<LlmProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google Gemini',
};

/** Which server-side API key env var the worker needs for each provider. */
export const PROVIDER_KEY_ENV: Record<LlmProvider, string> = {
  anthropic: 'ANTHROPIC_API_KEY',
  openai: 'OPENAI_API_KEY',
  google: 'GEMINI_API_KEY',
};

export const LLM_MODELS: LlmModel[] = [
  // ── Anthropic ────────────────────────────────────────────────────────
  {
    id: 'claude-opus-4-8',
    label: 'Claude Opus 4.8',
    provider: 'anthropic',
    blurb: 'Most capable — best for nuanced reasoning and proposals.',
    recommended: true,
  },
  {
    id: 'claude-sonnet-5',
    label: 'Claude Sonnet 5',
    provider: 'anthropic',
    blurb: 'Balanced speed and capability for everyday agent work.',
  },
  {
    id: 'claude-haiku-4-5-20251001',
    label: 'Claude Haiku 4.5',
    provider: 'anthropic',
    blurb: 'Fast and economical for high-volume, simpler runs.',
  },
  // ── OpenAI ───────────────────────────────────────────────────────────
  {
    id: 'gpt-5.6-sol',
    label: 'GPT-5.6 Sol',
    provider: 'openai',
    blurb: 'Frontier model for complex professional work.',
  },
  {
    id: 'gpt-5.6-terra',
    label: 'GPT-5.6 Terra',
    provider: 'openai',
    blurb: 'Balances intelligence and cost for general applications.',
  },
  {
    id: 'gpt-5.6-luna',
    label: 'GPT-5.6 Luna',
    provider: 'openai',
    blurb: 'Budget tier for high-volume, latency-sensitive tasks.',
  },
  // ── Google Gemini ────────────────────────────────────────────────────
  {
    id: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    provider: 'google',
    blurb: 'Frontier agentic/coding performance — Google stable flagship.',
  },
  {
    id: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro',
    provider: 'google',
    blurb: 'Maximum capability for complex problem-solving (preview).',
  },
  {
    id: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    provider: 'google',
    blurb: 'Deep reasoning and coding for complex tasks.',
  },
];

/** The default model id for a fresh instance (the one flagged recommended). */
export const DEFAULT_MODEL_ID: string =
  LLM_MODELS.find((m) => m.recommended)?.id ?? LLM_MODELS[0].id;

/** Look up a model by id. */
export function findModel(id: string | undefined | null): LlmModel | undefined {
  if (!id) return undefined;
  return LLM_MODELS.find((m) => m.id === id);
}

/** True when `id` is a known catalog model. */
export function isKnownModel(id: string | undefined | null): boolean {
  return Boolean(findModel(id));
}

/**
 * Which provider serves a model id. Prefers the catalog; falls back to a
 * prefix heuristic so an unknown-but-well-named id still routes sensibly
 * (kept in sync with the worker's own `providerForModel`).
 */
export function providerForModel(id: string): LlmProvider {
  const known = findModel(id);
  if (known) return known.provider;
  if (id.startsWith('claude')) return 'anthropic';
  if (id.startsWith('gpt') || id.startsWith('o')) return 'openai';
  if (id.startsWith('gemini')) return 'google';
  return 'anthropic';
}

/** Catalog grouped by provider, in declaration order — for grouped pickers. */
export function modelsByProvider(): { provider: LlmProvider; models: LlmModel[] }[] {
  const order: LlmProvider[] = ['anthropic', 'openai', 'google'];
  return order
    .map((provider) => ({
      provider,
      models: LLM_MODELS.filter((m) => m.provider === provider),
    }))
    .filter((g) => g.models.length > 0);
}
